import PropTypes from 'prop-types';
import React, { useMemo, useState } from 'react';
import { H4 } from '@blueprintjs/core';
import withAncestry from 'ecommerce/hoc/withAncestry';
import useModalContext from 'components/context/modal';
import classNames from 'classnames';
import produce from 'utils/produce';
import uniq from 'utils/uniq';
import useUserContext from 'ecommerce/context/user';

import useEditorContext from '../../../context/editorContext';
import useEditorItemContext from '../../../context/editorItemContext';
import { generateAncestryRecordsData } from '../../../generator/generateAncestryData';
import DialogPanelBody from '../../components/dialogPanelBody';
import AddPageButton from '../../components/addPageButton';
import SelectedPersonsCounter from '../../components/selectedPersonsCounter';
import AncestryRecordsGrid from './ancestryRecordsGrid';
import ancestryApi from '../../../api/ancestry';

const RecordSelectPanel = ({ selectedPersons, onAddPages }) => {
  const { accessToken, ancestryAuth } = useUserContext();
  const modalContext = useModalContext();
  const editorContext = useEditorContext();
  const { item, template } = useEditorItemContext();

  const [selectedRecords, setSelectedRecords] = useState({});

  const selectedIds = useMemo(() => {
    return Object.values(selectedRecords).map(record => record.id);
  }, [selectedRecords]);

  const selectedRecordsPerPerson = useMemo(() => {
    return produce({}, draft => {
      Object.values(selectedRecords).forEach(record => {
        if (!draft[record.person.id]) {
          draft[record.person.id] = {};
        }
        draft[record.person.id][record.id] = record;
      });
    });
  }, [selectedRecords]);

  const handleSelect = ({ ancestryRecord, previewWidth, previewHeight, person }) => {
    setSelectedRecords(value =>
      produce(value, draft => {
        const id = `${person.id}-${ancestryRecord.id}`;
        if (value[id]) {
          delete draft[id];
        } else {
          draft[id] = {
            ...ancestryRecord,
            previewWidth,
            previewHeight,
            person,
          };
        }
      }),
    );
  };

  const handleClick = async () => {
    const loadingModalKey = modalContext.spinner(<>Assembling your creation ...</>, {
      title: 'Building your project',
    });

    const personIds = [];
    const ancestryRecords = Object.values(selectedRecords).filter(record => {
      if (record) {
        personIds.push(record.person.id);
        return true;
      }
      return false;
    });

    const { data: ancestryPersons, error: detailedPersonsEror } = await ancestryApi(
      accessToken,
      ancestryAuth,
    ).getDetailedPersons(uniq(personIds));

    if (detailedPersonsEror) {
      modalContext.close(loadingModalKey);
      modalContext.confirm(<>{detailedPersonsEror}</>, {
        title: 'Error creating your project',
        closeable: false,
        confirmLabel: 'OK',
      });
      return;
    }

    const { data, error } = await generateAncestryRecordsData(
      editorContext.accessToken,
      template,
      ancestryRecords,
      ancestryPersons,
    );

    if (error) {
      modalContext.close(loadingModalKey);
      modalContext.confirm(<>{error}</>, {
        title: 'Error creating your project',
        closeable: false,
        confirmLabel: 'OK',
      });
      return;
    }

    editorContext.addPhotos(item.id, data.photos);
    // TODO filter pages
    const pages = data.pages.filter(page => true);

    modalContext.close(loadingModalKey);

    if (pages.length > 0) {
      onAddPages(pages, { sortByTags: true });
    } else {
      modalContext.confirm(<>Sorry, we didn&apos;t find any records.</>, {
        title: 'No pages added',
        closeable: false,
        confirmLabel: 'OK',
      });
    }
  };

  const enabled = selectedIds.length > 0;

  return (
    <DialogPanelBody
      actionButton={
        <>
          <SelectedPersonsCounter label="record" selectedPersons={selectedIds} />
          <AddPageButton label="Add Record Page(s)" disabled={!enabled} onClick={handleClick} />
        </>
      }
    >
      <div style={{ height: 510, marginBottom: 20, overflow: 'auto' }}>
        {selectedPersons.map((person, index) => (
          <div
            key={person.id}
            className={classNames({
              'mt-3': index !== 0,
            })}
          >
            <H4>{person.name}</H4>
            <AncestryRecordsGrid
              selectedIds={selectedRecordsPerPerson[person.id]}
              columnCount={6}
              person={person}
              onSelect={handleSelect}
            />
          </div>
        ))}
      </div>
    </DialogPanelBody>
  );
};

RecordSelectPanel.propTypes = {
  selectedPersons: PropTypes.array.isRequired,
  onAddPages: PropTypes.func.isRequired,
};

export default withAncestry(RecordSelectPanel);
